/**
 * Auth Domain
 * The session a join makes: signing in, the steps of the join, the match it
 * names — which the match domain keeps once joined (openMatch) — or the
 * refusal that stopped it, and the full screen it asks for.
 *
 * The auth service (services/authentication/auth-service.ts) drives a join
 * and says what happens through the handlers below, which write the store, as
 * the socket service says what happens to the socket through the
 * connection-lost domain's: the service writes no store itself
 * (design-checks' store-written-outside-domain holds it to that). The full
 * reset closes the session through them too (handleSessionClosed).
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Session } from '@heroiclabs/nakama-js';

import { NOT_STARTED_WAIT, waitsForStart, type JoinRefusal } from './join-refusals';
import { openMatch, type NakamaMatch } from './match';

/**
 * Connection status types
 */
type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

/**
 * The four steps of a join, in order: signing in, the account's team and
 * name, the socket, the match. A test session's team code is resolved within
 * the first.
 */
export type ConnectionStep = 'authenticating' | 'updatingAccount' | 'connectingSocket' | 'joiningMatch';

/**
 * Authentication state interface
 */
interface AuthState {
  // Connection state
  isConnecting: boolean;
  connectionStatus: ConnectionStatus;
  connectionStep: ConnectionStep | null;
  /** What refused the last join, said by its code (domains/join-refusals.ts). */
  refusal: JoinRefusal | null;
  /**
   * While a way in waits for its session to start (R1): the moment, in
   * milliseconds, until which it asks again by itself. Null when it is not
   * waiting.
   */
  waitUntil: number | null;
  /**
   * The player left the match from the menu (S1): a way in that joins by
   * itself — an admin launch, a student's link — says so and waits on Rejoin.
   * A reload joins again, as the link asks.
   */
  leftMatch: boolean;

  // Session and match data
  session: Session | null;
  isModerator: boolean;
  /** When false, skip match fullscreen kickoff and gate (admin hub / user-select joins). */
  enforceMatchFullscreen: boolean;

  // Setters
  setConnectionStatus: (status: ConnectionStatus) => void;
  setConnectionStep: (step: ConnectionStep | null) => void;
  setRefusal: (refusal: JoinRefusal | null) => void;
  setWaitUntil: (waitUntil: number | null) => void;
  setLeftMatch: (leftMatch: boolean) => void;
  setIsConnecting: (isConnecting: boolean) => void;
  setSession: (session: Session | null) => void;
  setIsModerator: (isModerator: boolean) => void;
  setEnforceMatchFullscreen: (enforce: boolean) => void;
}

/**
 * Initial state for the auth store
 */
const initialState = {
  isConnecting: false,
  connectionStatus: 'disconnected' as ConnectionStatus,
  connectionStep: null,
  refusal: null,
  waitUntil: null,
  leftMatch: false,
  session: null,
  isModerator: false,
  enforceMatchFullscreen: true,
};

/**
 * Authentication store
 * Manages the connection's state and the session
 */
export const useAuthStore = create<AuthState>()(
  devtools(
    (set) => ({
      ...initialState,

      /**
       * Set connection status
       */
      setConnectionStatus: (status) => {
        set(
          { connectionStatus: status, isConnecting: status === 'connecting' },
          false,
          'setConnectionStatus'
        );
      },

      /**
       * Set connection step message
       */
      setConnectionStep: (step) => {
        set({ connectionStep: step }, false, 'setConnectionStep');
      },

      /**
       * Set what refused the last join
       */
      setRefusal: (refusal) => {
        set({ refusal }, false, 'setRefusal');
      },

      /**
       * Set until when a way in waits for its session to start
       */
      setWaitUntil: (waitUntil) => {
        set({ waitUntil }, false, 'setWaitUntil');
      },

      /**
       * Set whether the player left the match from the menu
       */
      setLeftMatch: (leftMatch) => {
        set({ leftMatch }, false, 'setLeftMatch');
      },

      /**
       * Set isConnecting flag
       */
      setIsConnecting: (isConnecting) => {
        set({ isConnecting }, false, 'setIsConnecting');
      },

      /**
       * Set session
       */
      setSession: (session) => {
        set({ session }, false, 'setSession');
      },

      /**
       * Set isModerator flag
       */
      setIsModerator: (isModerator) => {
        set({ isModerator }, false, 'setIsModerator');
      },

      setEnforceMatchFullscreen: (enforce) => {
        set({ enforceMatchFullscreen: enforce }, false, 'setEnforceMatchFullscreen');
      },
    }),
    {
      name: 'auth-store',
    }
  )
);

// ==================== HANDLERS ====================

/**
 * A join starts (authService's join): the connection is said to be under way
 * at its first step, signing in, and the last join's refusal goes. A way in
 * waiting for its session to start keeps its wait; a player's press starts
 * afresh. Asking for the match's full screen is the way in's to say.
 */
export function handleJoinStarted({ enforceFullscreen, waiting }: { enforceFullscreen: boolean; waiting: boolean }): void {
  const auth = useAuthStore.getState();
  auth.setEnforceMatchFullscreen(enforceFullscreen);
  auth.setConnectionStatus('connecting');
  auth.setIsConnecting(true);
  auth.setRefusal(null);
  auth.setLeftMatch(false);
  if (!waiting) auth.setWaitUntil(null);
  auth.setConnectionStep('authenticating');
}

/** The join's next step starts: the account, the socket, the match. */
export function handleJoinStep(step: ConnectionStep): void {
  useAuthStore.getState().setConnectionStep(step);
}

/** Signed in: the session is the player's from now on. */
export function handleSignedIn(session: Session): void {
  useAuthStore.getState().setSession(session);
}

/** A moderator joined from the portal's link: the game screen draws the moderator's view. */
export function handleModeratorJoined(): void {
  useAuthStore.getState().setIsModerator(true);
}

/**
 * Joined: the match opens — the match domain keeps it, the one messages go
 * to from now on, its label naming its world (openMatch) — a wait for the
 * session to start is over, and the game screen mounts.
 */
export function handleJoined(match: NakamaMatch): void {
  const joined = useAuthStore.getState();
  openMatch(match);
  joined.setWaitUntil(null);
  joined.setConnectionStatus('connected');
  joined.setIsConnecting(false);
  joined.setConnectionStep(null);
}

/**
 * Refused: the refusal is kept by its code for the way in to say. A session
 * not started yet starts a wait — the way in asks again by itself until
 * NOT_STARTED_WAIT runs out — or keeps the one it is in; any other refusal
 * ends it.
 */
export function handleJoinRefused(refusal: JoinRefusal): void {
  const refused = useAuthStore.getState();
  refused.setWaitUntil(waitsForStart(refusal) ? (refused.waitUntil ?? Date.now() + NOT_STARTED_WAIT.forMs) : null);
  refused.setRefusal(refusal);
  refused.setConnectionStatus('error');
  refused.setIsConnecting(false);
  refused.setConnectionStep(null);
}

/** The player left the match from the menu (S1): a way in that joins by itself waits on Rejoin. */
export function handleLeftMatch(): void {
  useAuthStore.getState().setLeftMatch(true);
}

/**
 * The session is closed in this tab — the full reset (connection-reset.ts),
 * after Connection lost or as the player leaves: no session, no match and no
 * join under way, so the game screen unmounts. Whether the player left from
 * the menu, is a moderator and asks for full screen stay as they were.
 */
export function handleSessionClosed(): void {
  const auth = useAuthStore.getState();
  auth.setConnectionStatus('disconnected');
  auth.setIsConnecting(false);
  auth.setRefusal(null);
  auth.setWaitUntil(null);
  auth.setConnectionStep(null);
  auth.setSession(null);
}
